import type { Language } from './LanguageContext';
import type { TextKey } from './es';
// KNOWN TEMPORARY EDGE (shared → domain), the only one left after Phase 1.
// `flagStatusKey` is a conditions-specific mapper that has not moved yet;
// Phase 4 takes it (with `flagKey`, `windLevelKey`, `readableReason`,
// `translateOperator` and `noForecastFragment`) to modules/conditions/ui,
// leaving only `translateApiText` + API_TABLES here. The disable goes with it.
// eslint-disable-next-line no-restricted-imports
import type { FlagStatus } from '../../utils/beachHelpers';

/**
 * es→en translation of the texts coming from the backend (AEMET,
 * OpenWeather, Cruz Roja and those generated by the ranking). The backend
 * only speaks Spanish; in English we translate the known values on the
 * client and leave anything we do not recognize intact (fallback).
 *
 * Compound phrases (razonRanking, motivoBaja) are fragments
 * joined by commas — they are translated fragment by fragment.
 */

// Sky states (AEMET / OpenWeather vocabulary)
const SKY_MAP: Record<string, string> = {
  'despejado': 'clear',
  'soleado': 'sunny',
  // Short words generated by the backend ranking (razonRanking)
  'sol': 'sun',
  'parcialmente soleado': 'partly sunny',
  // Their night counterparts: after sunset there is no sun to name, so the
  // same sky is "Despejado" (`skyWord` in utils/beachHelpers).
  'parcialmente despejado': 'partly clear',
  'poco nuboso': 'partly cloudy',
  'intervalos nubosos': 'cloudy intervals',
  'intervalos nubosos con lluvia escasa': 'cloudy intervals with light rain',
  'intervalos nubosos con lluvia': 'cloudy intervals with rain',
  'parcialmente nuboso': 'partly cloudy',
  'nuboso': 'cloudy',
  'nublado': 'cloudy',
  'muy nuboso': 'very cloudy',
  'cubierto': 'overcast',
  'cubierto con lluvia escasa': 'overcast with light rain',
  'cubierto con lluvia': 'overcast with rain',
  'cielo nublado': 'cloudy sky',
  'cielo despejado': 'clear sky',
  'cielo cubierto': 'overcast sky',
  'algo de nubes': 'a few clouds',
  'nubes': 'clouds',
  'nubes dispersas': 'scattered clouds',
  // OpenWeather in Spanish says "cielo claro", not "cielo despejado" (which is
  // what AEMET says). It is the detail headline on any sunny day, so
  // the leak was visible on the first line of the page. Detected by running the app.
  'cielo claro': 'clear sky',
  'lluvia': 'rain',
  'lluvia ligera': 'light rain',
  'lluvia escasa': 'light rain',
  'llovizna': 'drizzle',
  'chubascos': 'showers',
  'chubascos tormentosos': 'thundery showers',
  'tormenta': 'storm',
  'niebla': 'fog',
  'bruma': 'mist',
  'neblina': 'haze',
  'nieve': 'snow',
};

/**
 * Cardinal directions. Used standalone (as a fragment) and as the second half
 * of AEMET's compound descriptions ("flojo del noreste").
 * `nordeste`/`sudoeste` are the variants AEMET also emits.
 */
const WIND_DIRECTION: Record<string, string> = {
  'norte': 'north',
  'sur': 'south',
  'este': 'east',
  'oeste': 'west',
  'noreste': 'northeast',
  'nordeste': 'northeast',
  'noroeste': 'northwest',
  'sureste': 'southeast',
  'suroeste': 'southwest',
  'sudoeste': 'southwest',
};

/** Wind intensities, first half of the compound descriptions. */
const WIND_INTENSITY: Record<string, string> = {
  'en calma': 'calm',
  'calma': 'calm',
  'flojo': 'light',
  'moderado': 'moderate',
  'fresco': 'fresh',
  'fuerte': 'strong',
  'muy fuerte': 'very strong',
};

// Wind (descriptions and fragments)
const WIND_MAP: Record<string, string> = {
  ...WIND_DIRECTION,
  ...WIND_INTENSITY,
  'viento flojo': 'light wind',
  'viento moderado': 'moderate wind',
  // `BeachScorer` emits this literal in `razonRanking`. The standalone form
  // ('fresco') is NOT added here: `FEELS_LIKE_MAP` overrides it (see note at the merge).
  'viento fresco': 'fresh wind',
  'viento fuerte': 'strong wind',
  'sin viento': 'no wind',
  'brisa suave': 'gentle breeze',
  'brisa': 'breeze',
  'variable': 'variable',
};

// Surf / sea state
const WAVES_MAP: Record<string, string> = {
  'oleaje débil': 'light surf',
  'oleaje moderado': 'moderate surf',
  'oleaje fuerte': 'heavy surf',
  'débil': 'light',
  'mar rizada': 'rippled sea',
  'rizada': 'rippled',
  'marejadilla': 'slight sea',
  'marejada': 'moderate sea',
  'fuerte marejada': 'rough sea',
  'mar gruesa': 'rough sea',
  'mar de fondo': 'groundswell',
  // Derived from wind in the backend (`wavesFromWind`, `wavesTextFromWind`).
  'agitado': 'choppy',
  'tranquilo': 'calm',
  // Rest of AEMET's Douglas scale. Careful: standalone 'moderado' and
  // 'fuerte' are NOT added, they already resolve via WIND_MAP — adding them here
  // would override them due to the spread order.
  'llana': 'calm sea',
  'gruesa': 'rough sea',
  'muy gruesa': 'very rough sea',
  'arbolada': 'high sea',
  'montañosa': 'very high sea',
  'enorme': 'phenomenal sea',
};

// Thermal sensation / temperature
const FEELS_LIKE_MAP: Record<string, string> = {
  'muy alta': 'very hot',
  'alta': 'hot',
  'agradable': 'pleasant',
  'fresca': 'cool',
  'fría': 'cold',
  'temperatura agradable': 'pleasant temperature',
  'temperatura fresca': 'cool temperature',
  'temperatura muy alta': 'very hot temperature',
  'calor': 'hot',
  'frío': 'cold',
  'fresco': 'cool',
  'muy frío': 'very cold',
  'muy caluroso': 'very hot',
  'caluroso': 'hot',
  // Full scale of `sensationFromTemp` in the backend.
  'templado': 'mild',
  'calor moderado': 'warm',
  'calor intenso': 'very hot',
};

// UV levels ("muy alto" after stripping the "índice ultravioleta" prefix)
const UV_MAP: Record<string, string> = {
  'bajo': 'low',
  'medio': 'moderate',
  // 'moderado' (AEMET's wording for the 3-5 band) is NOT added here: it already
  // resolves through WIND_MAP, and repeating it would shadow that entry.
  // The collision test in apiText.test.ts is what caught the attempt.
  'alto': 'high',
  'muy alto': 'very high',
  'extremo': 'extreme',
  // The variants with the full prefix have been removed: `BeachDetailPage`
  // trims it before translating, so they were never actually looked up.
};

// Fragments generated by the backend ranking (razonRanking / motivoBaja)
const RANKING_MAP: Record<string, string> = {
  'sin cobertura cruz roja': 'no Red Cross coverage',
  'bandera roja': 'red flag',
  'bandera amarilla': 'yellow flag',
  'bandera verde': 'green flag',
  'lloviendo ahora': 'raining now',
  'lluvia en la última hora': 'rain in the last hour',
  'lluvia o tormenta': 'rain or storm',
  'lluvia prevista': 'rain expected',
  // Outlook for the next few hours (backend `WeatherOutlook`). Qualitative and
  // without an hour on purpose: these fragments are translated by exact match,
  // so a time inside them could not be translated at all.
  'mejora en las próximas horas': 'improving in the next few hours',
  'empeora en las próximas horas': 'getting worse in the next few hours',
  'precaución': 'caution',
  'oleaje peligroso': 'dangerous surf',
  'aviso litoral': 'coastal warning',
  'aviso costero activo': 'active coastal warning',
  'sin peligro': 'no danger',
  'peligro': 'danger',
  'riesgo': 'risk',
  'riesgo importante': 'significant risk',
  'riesgo extremo': 'extreme risk',
  // Fragments from `buildCautionReason` / `buildDowngradeFactors`.
  'condiciones aceptables': 'acceptable conditions',
  'baño prohibido': 'swimming prohibited',
  'uv muy alto': 'very high UV',
  'temperatura baja': 'low temperature',
  'condiciones poco favorables': 'unfavourable conditions',
  // Exclusion reasons: they arrive as a full string, without commas.
  'baño prohibido (bandera negra)': 'swimming prohibited (black flag)',
  'bandera roja con viento muy fuerte': 'red flag with very strong wind',
  'tormenta activa': 'active storm',
  'alerta meteorológica': 'weather alert',
  'condiciones peligrosas': 'dangerous conditions',
};

// Flag colors as they arrive from Cruz Roja ("Verde"/"Amarilla"/"Roja")
const COLOR_MAP: Record<string, string> = {
  'verde': 'green',
  'amarilla': 'yellow',
  'roja': 'red',
  // The backend also emits these two (`flagToEs`), although today the interface
  // treats them as "no data" — see known-issues/blackFlag.test.tsx.
  'negra': 'black',
  'desconocida': 'unknown',
};

// Static beach information (tipoPlaya, arena, acceso, bus...)
const INFO_MAP: Record<string, string> = {
  'urbana': 'urban',
  'semiurbana': 'semi-urban',
  'aislada': 'secluded',
  'acantilados': 'cliffs',
  'arena dorada': 'golden sand',
  'arena dorada y grava': 'golden sand and gravel',
  'arena blanca': 'white sand',
  'arena gris': 'grey sand',
  'arena fina': 'fine sand',
  'arena oscura': 'dark sand',
  'grava': 'gravel',
  'bolos': 'pebbles',
  'roca': 'rock',
  'a pie': 'on foot',
  'en coche': 'by car',
  'en barco': 'by boat',
  'escaleras': 'stairs',
  'rampa': 'ramp',
  'urbano': 'urban',
  'interurbano': 'intercity',
  'sí': 'yes',
  'no': 'no',
  'más de 100 plazas': 'more than 100 spaces',
  'menos de 50 plazas': 'fewer than 50 spaces',
  'entre 50 y 100 plazas': '50-100 spaces',
  'no disponible': 'not available',
};

/**
 * Single lookup table. CAREFUL with the order: a repeated key is won by the
 * LAST table in the spread. Today only `'fresco'` collides, on purpose:
 * it is emitted by both wind and thermal sensation, and sensation wins
 * ('cool'). The wind side is resolved where there is context: with the
 * literal `'viento fresco'` and within the compound translation.
 *
 * The test `no hay colisiones entre tablas salvo la documentada` watches that no
 * other one sneaks in (e.g. a surf `'fuerte'` would override the wind one).
 */
const API_MAP: Record<string, string> = {
  ...SKY_MAP,
  ...WIND_MAP,
  ...WAVES_MAP,
  ...FEELS_LIKE_MAP,
  ...UV_MAP,
  ...RANKING_MAP,
  ...COLOR_MAP,
  ...INFO_MAP,
};

/** Tables exposed only for the test that watches for collisions. */
export const API_TABLES = {
  SKY_MAP,
  WIND_MAP,
  WAVES_MAP,
  FEELS_LIKE_MAP,
  UV_MAP,
  RANKING_MAP,
  COLOR_MAP,
  INFO_MAP,
};

/** Preserves the initial capitalization of the original text. */
function keepCapital(original: string, translation: string): string {
  if (!original || !translation) return translation;
  const startsUppercase = original.charAt(0) === original.charAt(0).toUpperCase();
  if (startsUppercase) {
    return translation.charAt(0).toUpperCase() + translation.slice(1);
  }
  return translation;
}

/**
 * Connectors between intensity and direction, from most specific to least.
 * The bare space goes last so that "flojo variable" also splits.
 */
const WIND_CONNECTORS = [' del ', ' de ', ' '];

/**
 * Translates AEMET's compound wind descriptions ("flojo del noreste"),
 * which cannot be enumerated: they are intensity × direction and the text is free-form.
 *
 * SAFETY PROPERTY: it only returns something if ALL parts are known.
 * If any fails, it returns null and the fragment is left intact. That is
 * what prevents mangling free text containing " de " or " del ", such as the
 * `acceso` field ("A pie por el recinto de la península de La Magdalena").
 *
 * Returns null if it does not recognize the form.
 */
function translateCompoundWind(fragment: string): string | null {
  const low = fragment.toLowerCase();

  for (const connector of WIND_CONNECTORS) {
    // The direction is always the last token, hence lastIndexOf.
    const cutoff = low.lastIndexOf(connector);
    if (cutoff < 0) continue;

    const left = low.slice(0, cutoff).replace(/^viento /, '');
    const right = low.slice(cutoff + connector.length).replace(/^componente /, '');

    const intensity = WIND_INTENSITY[left];
    if (!intensity) continue;

    if (right === 'variable') return `${intensity} variable wind`;

    const direction = WIND_DIRECTION[right];
    if (direction) return `${intensity} wind from the ${direction}`;
  }

  return null;
}

function translateFragment(fragment: string): string {
  if (!fragment) return fragment;
  const direct = API_MAP[fragment.toLowerCase()];
  if (direct) return keepCapital(fragment, direct);

  const wind = translateCompoundWind(fragment);
  if (wind) return keepCapital(fragment, wind);

  // "sin cobertura <operador>": the operator's name is data, so it cannot be
  // enumerated here. Translating the frame and leaving the name intact keeps
  // any future region readable instead of leaving the whole phrase in Spanish.
  const coverage = fragment.match(/^sin cobertura (.+)$/i);
  if (coverage) {
    // Only reachable in English: translateApiText returns early for Spanish.
    return keepCapital(fragment, `no ${translateOperator(coverage[1], 'en')} coverage`);
  }

  // Numeric fragment ("19°", "09:00 - 21:00") or free text → intact
  return fragment;
}

/**
 * Translates an API text into the active language. In Spanish it returns the
 * original; in English it tokenizes by commas and translates each known
 * fragment, leaving the rest as is.
 */
export function translateApiText(text: string | null | undefined, language: Language): string {
  if (!text) return '';
  if (language === 'es') return text;
  return text
    .split(',')
    .map((fragment) => translateFragment(fragment.trim()))
    .join(', ');
}

/**
 * Prepends "viento" to "flojo/fuerte" in the raw ranking reason, so it reads
 * "viento flojo" instead of "flojo". Operates on the API's Spanish, ALWAYS before
 * translating with translateApiText.
 */
export function readableReason(rankingReason: string): string {
  return rankingReason.replace(/(?<!viento )\b(flojo|fuerte)\b/i, 'viento $1');
}

/**
 * Drops "mejora / empeora en las próximas horas" from a reason.
 *
 * The backend says it in `razonRanking` and `motivoBaja`, and `TrendBadge` says
 * it too. Wherever both are painted the fragment has to go, or the card reads
 * "Sol, 22°, mejora en las próximas horas" with "Mejora · se despeja" right
 * underneath. It is removed HERE and not in the API because that text is a
 * contract other clients read. Always applied to the API's Spanish, BEFORE
 * `translateApiText`.
 */
export function noForecastFragment(rationale: string): string {
  return rationale
    .split(',')
    .filter((f) => !/\b(mejora|empeora) en las próximas horas\b/i.test(f.trim()))
    .join(',')
    .replace(/^\s*,\s*/, '')
    .trim();
}

/**
 * Names of the lifeguard operators that have an established name in English.
 * Anything else is a proper noun and is left alone — better an untranslated
 * name than an invented one.
 */
const OPERATOR_MAP: Record<string, string> = {
  'cruz roja': 'Red Cross',
};

/** Operator name ("Cruz Roja") as it must be read in the active language. */
export function translateOperator(operator: string, language: Language): string {
  if (language === 'es') return operator;
  return OPERATOR_MAP[operator.trim().toLowerCase()] ?? operator;
}

/** Dictionary key for the Cruz Roja flag (replaces flagDisplayText). */
export function flagKey(flag?: string): TextKey {
  const b = flag?.toLowerCase() || '';
  if (b.includes('negra')) return 'bandera.negra';
  if (b.includes('roja')) return 'bandera.roja';
  if (b.includes('amarilla')) return 'bandera.amarilla';
  if (b.includes('verde')) return 'bandera.verde';
  return 'bandera.sinDatos';
}

/**
 * Dictionary key according to the flag state: actual color, "out of
 * hours" (no flag outside surveillance) or "no data".
 */
export function flagStatusKey(status: FlagStatus, flag?: string): TextKey {
  if (status === 'color') return flagKey(flag);
  if (status === 'fueraDeHorario') return 'bandera.fueraDeHorario';
  return 'bandera.sinDatos';
}

/** Dictionary key for the wind level according to speed in m/s. */
export function windLevelKey(ms: number): TextKey {
  if (ms < 3) return 'viento.sinViento';
  if (ms < 6) return 'viento.brisaSuave';
  if (ms < 10) return 'viento.moderado';
  return 'viento.fuerte';
}
